import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const ExportSearchResults = ({ results = [], searchQuery = '', searchType = '' }) => {
  const [exporting, setExporting] = useState(false);

  if (!results.length) return null;

  const exportToPDF = async () => {
    setExporting(true);
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;
      const contentWidth = pageWidth - 2 * margin;
      let y = 20;

      // Title
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('InfoPilot Explorer - Search Results', margin, y);
      y += 8;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Query: "${searchQuery}" | Type: ${searchType} | Results: ${results.length}`, margin, y);
      y += 4;
      doc.text(`Exported: ${new Date().toLocaleString()}`, margin, y);
      y += 8;

      // Draw line
      doc.setDrawColor(124, 58, 237);
      doc.setLineWidth(0.5);
      doc.line(margin, y, pageWidth - margin, y);
      y += 6;

      // Results
      results.forEach((r, i) => {
        if (y > 270) { doc.addPage(); y = 20; }

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        const title = `${i + 1}. ${(r.title || 'Untitled').substring(0, 80)}`;
        doc.text(title, margin, y);
        y += 5;

        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(59, 130, 246);
        const url = (r.url || '').substring(0, 90);
        doc.text(url, margin, y);
        y += 4;

        doc.setTextColor(0, 0, 0);
        if (r.snippet) {
          const snippet = r.snippet.substring(0, 200);
          const lines = doc.splitTextToSize(snippet, contentWidth);
          doc.text(lines, margin, y);
          y += lines.length * 3.5;
        }

        if (r.categories && r.categories.length) {
          doc.setTextColor(124, 58, 237);
          doc.text(`Categories: ${r.categories.slice(0, 5).join(', ')}`, margin, y);
          y += 4;
          doc.setTextColor(0, 0, 0);
        }

        y += 4;
      });

      doc.save(`infopilot-results-${searchQuery.replace(/\s+/g, '-').substring(0, 30)}.pdf`);
    } catch (e) {
      console.error('PDF export failed:', e);
    }
    setExporting(false);
  };

  const exportToExcel = () => {
    setExporting(true);
    try {
      const data = results.map((r, i) => ({
        '#': i + 1,
        'Title': r.title || '',
        'URL': r.url || '',
        'Snippet': (r.snippet || '').substring(0, 500),
        'Type': r.article_type || r.searchType || '',
        'Categories': (r.categories || []).join(', '),
        'Quality Score': r.content_quality_score || r.quality_score || '',
        'Domain': r.domain || ''
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      ws['!cols'] = [
        { wch: 4 }, { wch: 50 }, { wch: 60 }, { wch: 80 },
        { wch: 15 }, { wch: 30 }, { wch: 12 }, { wch: 25 }
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Search Results');

      const metaWs = XLSX.utils.json_to_sheet([{
        'Query': searchQuery,
        'Search Type': searchType,
        'Total Results': results.length,
        'Exported': new Date().toLocaleString()
      }]);
      XLSX.utils.book_append_sheet(wb, metaWs, 'Search Info');

      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([wbout], { type: 'application/octet-stream' }),
        `infopilot-results-${searchQuery.replace(/\s+/g, '-').substring(0, 30)}.xlsx`);
    } catch (e) {
      console.error('Excel export failed:', e);
    }
    setExporting(false);
  };

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }} data-testid="export-results-container">
      <button onClick={exportToPDF} disabled={exporting}
        data-testid="export-pdf-btn"
        style={{
          background: 'rgba(239, 68, 68, 0.12)', border: '1px solid rgba(239, 68, 68, 0.3)',
          color: '#ef4444', padding: '5px 10px', borderRadius: 7, cursor: 'pointer',
          fontSize: '0.7rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4,
          transition: 'all 0.15s', opacity: exporting ? 0.5 : 1, minHeight: 32
        }}>
        <i className="fas fa-file-pdf" /> PDF
      </button>
      <button onClick={exportToExcel} disabled={exporting}
        data-testid="export-excel-btn"
        style={{
          background: 'rgba(16, 185, 129, 0.12)', border: '1px solid rgba(16, 185, 129, 0.3)',
          color: '#10b981', padding: '5px 10px', borderRadius: 7, cursor: 'pointer',
          fontSize: '0.7rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4,
          transition: 'all 0.15s', opacity: exporting ? 0.5 : 1, minHeight: 32
        }}>
        <i className="fas fa-file-excel" /> Excel
      </button>
    </div>
  );
};

export default ExportSearchResults;
